import { useEffect, useState } from 'react';
import type { LeadPatch, LeadRow, LeadStatusKey } from '@/api/types';
import { LEAD_STATUSES } from '@/api/types';
import { formatDateTime, formatDuration, formatMoney } from './format';
import { LeadStatusChip, STATUS_LABELS } from './LeadPipeline';
import { Select } from './Select';

interface LeadsTableProps {
  rows: LeadRow[];
  currency: string | null;
  /** False for a viewer — the edit control is hidden entirely rather than shown and 403-ing. */
  canManage: boolean;
  saving: boolean;
  /** Resolves when the save landed; rejects on a refusal — the editor stays open with the edits. */
  onSave: (id: string, patch: LeadPatch) => Promise<unknown>;
  /** Opens the full profile. Omitted where there is nowhere to navigate to. */
  onOpen?: (id: string) => void;
}

/**
 * The lead list, with the sales columns editable in place.
 *
 * <b>Editing expands a row rather than opening a dialog.</b> Qualifying a lead means reading what
 * it is — where it came from, how the conversation went — while you set the stage and the value.
 * A modal covers exactly that context, and this app has no dialog primitive to borrow, so the
 * form unfolds under the row it belongs to and the row above it stays legible.
 *
 * One row is editable at a time by construction: `editing` holds a single id.
 */
export function LeadsTable({ rows, currency, canManage, saving, onSave, onOpen }: LeadsTableProps) {
  const [editing, setEditing] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="empty-state t-body">
        No leads match this filter.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          Widen the date range, or clear the filters above.
        </div>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Captured</th>
            <th>Lead</th>
            <th>Source</th>
            <th>Country</th>
            <th className="num">First reply</th>
            <th className="num">Duration</th>
            <th className="num">AI</th>
            <th>Stage</th>
            <th className="num">Value</th>
            <th>Owner</th>
            {canManage && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LeadRowView
              key={row.id}
              row={row}
              currency={currency}
              canManage={canManage}
              saving={saving}
              isEditing={editing === row.id}
              onOpen={onOpen}
              onToggleEdit={() => setEditing(editing === row.id ? null : row.id)}
              onSave={async (patch) => {
                try {
                  await onSave(row.id, patch);
                  setEditing(null);
                } catch {
                  // Refused: keep the editor open with what was typed; the page shows why.
                }
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadRowView({
  row, currency, canManage, saving, isEditing, onToggleEdit, onSave, onOpen,
}: {
  row: LeadRow;
  currency: string | null;
  canManage: boolean;
  saving: boolean;
  isEditing: boolean;
  onToggleEdit: () => void;
  onSave: (patch: LeadPatch) => void;
  onOpen?: (id: string) => void;
}) {
  // Captured, Lead, Source, Country, First reply, Duration, AI, Stage, Value, Owner (+ Actions).
  // Must track the header row above: too small and the expanded editor stops short of the table
  // edge, which reads as a rendering glitch rather than a miscount.
  const columns = canManage ? 11 : 10;

  return (
    <>
      <tr>
        <td className="t-caption" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.captured_at)}</td>

        <td>
          {/* The name opens the profile; the row itself stays inert so the inline editor below it
              is not a click target you can trigger by aiming at a cell. */}
          {onOpen ? (
            <button type="button" className="linklike" onClick={() => onOpen(row.id)}>
              {row.name || row.email || '(anonymous)'}
            </button>
          ) : (
            <div style={{ fontWeight: 500 }}>{row.name || row.email || '(anonymous)'}</div>
          )}
          {row.name && row.email && <div className="t-caption subtle">{row.email}</div>}
          {row.utm_campaign && <div className="t-caption subtle">campaign: {row.utm_campaign}</div>}
        </td>

        <td>
          <span className={`chip ${row.source === 'chat' ? 'chip-src' : 'chip-on'}`}>
            {row.source === 'chat' ? row.channel || 'chat' : row.form_type || 'form'}
          </span>
          {/* A missed chat is the one status worth shouting about on the row: the lead arrived and
              nobody answered, which no amount of pipeline work later can undo. */}
          {row.is_missed && <span className="chip chip-down" style={{ marginInlineStart: 4 }}>missed</span>}
        </td>

        <td>{row.country || <span className="subtle">—</span>}</td>
        <td className="num">{formatDuration(row.first_response_seconds)}</td>
        <td className="num">{formatDuration(row.handling_seconds)}</td>
        <td className="num"><AiScoreCell row={row} /></td>
        <td><LeadStatusChip status={row.status} /></td>
        <td className="num">{formatMoney(row.value_amount, row.currency ?? currency)}</td>
        <td>{row.owner || <span className="subtle">—</span>}</td>

        {canManage && (
          <td className="num">
            <button className="btn btn-ghost" onClick={onToggleEdit}>
              {isEditing ? 'Close' : 'Edit'}
            </button>
          </td>
        )}
      </tr>

      {isEditing && (
        <tr>
          <td colSpan={columns} style={{ background: 'var(--bg-main)' }}>
            <LeadEditor row={row} currency={currency} saving={saving} onSave={onSave} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The sales-state form. Seeded from the row and reseeded whenever the row identity changes, so
 * reopening a different lead never shows the previous one's draft.
 */
function LeadEditor({
  row, currency, saving, onSave,
}: { row: LeadRow; currency: string | null; saving: boolean; onSave: (patch: LeadPatch) => void }) {
  const [status, setStatus] = useState<LeadStatusKey>(row.status);
  const [value, setValue] = useState(row.value_amount === null ? '' : String(row.value_amount));
  const [owner, setOwner] = useState(row.owner ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');

  useEffect(() => {
    setStatus(row.status);
    setValue(row.value_amount === null ? '' : String(row.value_amount));
    setOwner(row.owner ?? '');
    setNotes(row.notes ?? '');
  }, [row.id, row.status, row.value_amount, row.owner, row.notes]);

  const parsedValue = value.trim() === '' ? null : Number(value);
  const valueInvalid = parsedValue !== null && (!Number.isFinite(parsedValue) || parsedValue < 0);

  const submit = () => {
    if (valueInvalid) return;

    // An emptied field must CLEAR, and JSON null cannot say that — it is indistinguishable from
    // "not supplied" on the wire, so the API takes an explicit flag instead.
    onSave({
      status,
      value_amount: parsedValue ?? undefined,
      clear_value: parsedValue === null && row.value_amount !== null,
      currency: parsedValue !== null ? (row.currency ?? currency ?? undefined) : undefined,
      owner: owner.trim() || undefined,
      clear_owner: owner.trim() === '' && Boolean(row.owner),
      notes,
    });
  };

  return (
    <div className="lead-editor">
      <div className="lead-editor-fields">
        <Select
          label="Stage"
          value={status}
          onChange={(next) => setStatus(next as LeadStatusKey)}
          options={LEAD_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
        />

        <label className="field">
          <span className="t-label muted">Deal value{currency ? ` (${currency})` : ''}</span>
          <input
            className="input"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 15000"
            aria-invalid={valueInvalid}
          />
          {valueInvalid && <span className="t-caption" style={{ color: 'var(--error)' }}>Must be a positive number.</span>}
        </label>

        <label className="field">
          <span className="t-label muted">Owner</span>
          <input
            className="input"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="who is working it"
          />
        </label>
      </div>

      <label className="field">
        <span className="t-label muted">Notes</span>
        <textarea
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="what happened, what is next"
        />
      </label>

      <div className="lead-editor-meta">
        <span className="t-caption subtle">
          {row.source === 'chat'
            ? `${row.messages_in ?? 0} in / ${row.messages_out ?? 0} out${row.csat_score ? ` · CSAT ${row.csat_score}/5` : ''}`
            : row.landing_page
              ? `landed on ${row.landing_page}`
              : ''}
        </span>

        <button className="btn btn-primary" onClick={submit} disabled={saving || valueInvalid}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * The AI intent score, deliberately understated.
 *
 * Rendered in a muted, narrow column rather than as a headline number: it is a model's guess, and
 * the sales `status` beside it is what a human decided. Colour is used only at the extremes, where
 * the signal is strong enough to be worth acting on — a full green-to-red gradient would give a
 * 51 and a 49 visibly different weight they have not earned.
 *
 * The reason travels in the tooltip. A bare number invites a trust it cannot support; the
 * justification is what makes it checkable.
 */
function AiScoreCell({ row }: { row: LeadRow }) {
  const score = row.ai_score;

  if (score === null || score === undefined) {
    return <span className="subtle" title="Not scored yet">—</span>;
  }

  const tone =
    score >= 80 ? 'var(--success)' :
    score < 20 ? 'var(--text-subtle)' :
    'var(--text-main)';

  return (
    <span
      style={{ color: tone, fontWeight: score >= 80 ? 600 : 400 }}
      title={row.ai_score_reason ?? 'No reason recorded'}
    >
      {score}
    </span>
  );
}
